import classnames from "classnames";
import { isPresent, humanize, isNotEmpty } from "neetocist";
import { Crown } from "neetoicons";
import { Dropdown } from "neetoui";
import { useTranslation } from "react-i18next";

import HelpMenu from "./HelpMenu";
import LinkSection from "./LinkSection";
import OrganizationSwitcher from "./OrganizationSwitcher";
import ProductSwitcherMenu from "./ProductSwitcher";
import ProfileExpandMenu from "./ProfileExpandMenu";
import ThemeSwitcher from "./ThemeSwitcher";
import WorkspaceMenu from "./WorkspaceMenu";

import { NEETO_AUTH_BILLING_INFO_URL } from "../constants";

const ProfileMenu = ({
  profileInfo,
  showProductSwitcher,
  defaultLinks,
  topLinks,
  bottomLinks,
  customContent,
  toggleModal,
  isThemeSwitcherEnabled,
  isOrganizationSwitcherEnabled,
  isConsumer,
  enableSubscriptionUpgradeRequest,
  openUpgradeModal,
}) => {
  const { Menu, MenuItem, Divider } = Dropdown;
  const { isAuthenticated } = profileInfo;
  const { t } = useTranslation();

  const normalizedPlan = (
    globalProps.user?.subscriptionPlan || ""
  ).toLowerCase();

  const isFreePlan = normalizedPlan === "free";
  const isOwner = globalProps.user?.isOwner;
  const showUpgradeModal =
    isFreePlan && enableSubscriptionUpgradeRequest && !isOwner;

  const getSubscriptionPlanAction = () => {
    if (showUpgradeModal) return { onClick: openUpgradeModal };

    if (isFreePlan && isOwner && enableSubscriptionUpgradeRequest) {
      return {
        href: `${globalProps.organization?.authAppUrl}/admin/billing/overview`,
      };
    }

    return { href: NEETO_AUTH_BILLING_INFO_URL };
  };

  const subscriptionPlanLink = {
    label: (
      <>
        {globalProps.appName}{" "}
        <span
          className={classnames(
            "neeto-molecules-subscription-plan-badge flex items-center gap-1",
            {
              "neeto-molecules-subscription-plan-badge--pro":
                normalizedPlan === "pro",
              "neeto-molecules-subscription-plan-badge--free": isFreePlan,
            }
          )}
        >
          {normalizedPlan === "pro" ? (
            humanize(globalProps.user?.subscriptionPlan)
          ) : (
            <>
              <Crown size={12} />
              {t("neetoMolecules.sidebar.upgradePlan")}
            </>
          )}
        </span>
      </>
    ),
    "data-testid": "subscription-plan",
    ...getSubscriptionPlanAction(),
  };

  return (
    <Menu className="pb-1">
      {!isConsumer && (
        <>
          <LinkSection links={defaultLinks} />
          {isNotEmpty(topLinks) && (
            <>
              <Divider />
              <LinkSection links={topLinks} />
            </>
          )}
          {isPresent(customContent) && (
            <>
              <Divider />
              <MenuItem className="neeto-molecules-floating-action-button__profile-wrapper-custom-content">
                {customContent}
              </MenuItem>
            </>
          )}
          <Divider />
          {globalProps.user?.isOwner && <WorkspaceMenu />}
          {isOrganizationSwitcherEnabled && <OrganizationSwitcher />}
          <Divider />
          {isPresent(globalProps.user?.subscriptionPlan) && (
            <LinkSection links={[subscriptionPlanLink]} />
          )}
          {showProductSwitcher && <ProductSwitcherMenu {...{ toggleModal }} />}
          <HelpMenu />
          <Divider />
        </>
      )}
      {isAuthenticated && !isConsumer && (
        <ProfileExpandMenu {...{ profileInfo }} />
      )}
      <LinkSection links={bottomLinks} />
      {isThemeSwitcherEnabled && (
        <>
          <Divider />
          <ThemeSwitcher />
        </>
      )}
    </Menu>
  );
};

export default ProfileMenu;
